import ModalResponsive from '@/components/commons/modal-responsive';
import { ShippingMethodCheckoutType } from '@/components/sections/builder/elements/body/checkout-form/component/checkout-form';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { RadioGroup } from '@/components/ui/radio-group';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from '@/components/ui/use-toast';
import { useGetShippingRates, useShippingCategory } from '@/hooks/api/storefront/orders';
import { cn, formatEstimateDelivery, formatIDR } from '@/lib/utils';
import { IShippingRates } from '@/types/area';
import { BaseShippingRatesPayload } from '@/types/http/orders/common';
import { AxiosError } from 'axios';
import Image from 'next/image';
import { useCallback, useEffect, useState } from 'react';
import Cookies from 'js-cookie';
import { IStoreInfoData } from '@/types/general';
import { ThemeColorSchemeType } from '@/stores/builder';

export type FormSelectShippingCheckoutMethodType = {
  shippingMethodId: string;
  shippingServiceId?: string;
};

export interface shippingRatesProps extends BaseShippingRatesPayload {
  checkout_id?: string;
}

type ModalShippingCheckoutProps = {
  openModal: boolean;
  setOpenModal: (open: boolean) => void;
  dataRates: shippingRatesProps;
  onSelect: (values: ShippingMethodCheckoutType) => void;
  disabled?: boolean;
  storeSetting?: IStoreInfoData;
  colorScheme?: ThemeColorSchemeType;
};

export default function ModalShippingCheckout({
  openModal,
  setOpenModal,
  dataRates,
  onSelect,
  disabled,
  storeSetting,
  colorScheme,
}: ModalShippingCheckoutProps) {
  const findLocationId = Cookies.get('_location_id');

  const [selectedCategory, setSelectedCategory] = useState<string | undefined>(undefined);
  const [selectedShipping, setSelectedShipping] = useState<string | undefined>(undefined);
  const [listShipping, setListShipping] = useState<IShippingRates[]>([]);

  const { data: listShippingCategory, isLoading: isLoadingCategory } = useShippingCategory({
    enabled: !!openModal,
  });
  const { mutateAsync: getShippingRates, isPending: isLoadingShipping } = useGetShippingRates(
    findLocationId || '',
    {
      onSuccess: (response) => setListShipping(response.data as unknown as IShippingRates[]),
      onError: (error) => {
        setListShipping([]);
        setSelectedShipping(undefined);

        let message = 'Something went wrong';
        if (error instanceof AxiosError) {
          message = error.response?.data.message;
        }

        toast({
          title: 'Error',
          description: message,
          variant: 'destructive',
        });
      },
    }
  );

  const handleCategorySelect = (categoryId: string) => {
    if (!categoryId) return;

    const selectedCategory = listShippingCategory?.data.find(
      (item) => item.service_category_id === categoryId
    );

    if (
      selectedCategory?.name.toLowerCase() === 'instant' ||
      selectedCategory?.name.toLowerCase() === 'sameday'
    ) {
      if (disabled) {
        toast({
          title: 'Error',
          description: 'Please enter the pinpoint location for this shipping',
          variant: 'destructive',
        });
        return;
      }
    }

    setSelectedCategory(categoryId);

    if (categoryId) {
      getShippingRates({
        ...dataRates,
        service_category_id: Number(categoryId),
      });
    }
  };

  const handleShippingSelect = (serviceId: number) => {
    const selectedService = listShipping.find((item) => item.courier_service_id === serviceId);

    if (selectedService) {
      const result = {
        id: String(selectedService?.courier_id),
        name: selectedService?.courier_service_code,
        courir_category_id:
          storeSetting?.shipment_list_view === 'all'
            ? String(selectedService?.service_category_id)
            : selectedCategory,
        courier_code: selectedService?.courier_service_category,
        courier_logo: selectedService?.courier_logo,
        service: {
          id: String(selectedService?.courier_service_id),
          name: selectedService?.courier_service_name,
          label: `${selectedService?.eta_from} - ${selectedService?.eta_to}`,
          price: selectedService?.final_rates,
          shipping_insurance: selectedService?.shipping_insurance,
          is_cod_supported: selectedService?.is_cod_supported,
          cod_fee: selectedService?.cod_fee,
        },
        promotion: selectedService?.promotion,
        shipper_promotion: selectedService?.shipper_promotion,
        shipper_promotion_value:
          typeof selectedService?.shipper_promotion_value === 'number'
            ? selectedService.shipper_promotion_value
            : 0,
        shipper_discount_rates: selectedService?.shipper_discount_rates || 0,
      };

      onSelect(result);
    }

    setSelectedCategory(undefined);
  };

  const resetSelection = useCallback(() => {
    setSelectedCategory(undefined);
  }, []);

  useEffect(() => {
    if (openModal && storeSetting?.shipment_list_view === 'all') {
      getShippingRates(dataRates);
    }
  }, [openModal, storeSetting?.shipment_list_view]);

  return (
    <>
      <ModalResponsive
        openModal={openModal}
        setOpenModal={setOpenModal}
        title='Select Shipping'
        className='theme_font max-w-lg'
      >
        <div
          className='px-6 pb-5'
          style={
            {
              '--primary-text': colorScheme?.text || 'inherit',
              '--secondary-text': colorScheme?.textSecondary || 'inherit',
              '--background': colorScheme?.background || 'inherit',
            } as React.CSSProperties
          }
        >
          {storeSetting?.shipment_list_view === 'all' ? (
            <>
              {isLoadingShipping ? (
                <div className='flex w-full flex-col items-center justify-center gap-2 px-2'>
                  <Skeleton className='h-8 w-full rounded-sm bg-gray-200' />
                  <Skeleton className='h-8 w-full rounded-sm bg-gray-200' />
                  <Skeleton className='h-8 w-full rounded-sm bg-gray-200' />
                </div>
              ) : (
                <div className='max-h-96 overflow-y-auto px-2'>
                  <RadioGroup value={selectedShipping}>
                    {listShipping.map((courier) => {
                      const isInstantOrSameday = ['SAMEDAY', 'INSTANT'].includes(
                        courier.courier_service_category?.toUpperCase()
                      );
                      const disableCourier = isInstantOrSameday && disabled;

                      return (
                        <div
                          key={courier.courier_service_id}
                          className={cn(
                            'flex items-center space-x-2 rounded-lg border-b px-4 py-2',
                            disableCourier ? 'cursor-not-allowed opacity-50' : 'hover:bg-accent'
                          )}
                          onClick={() => {
                            if (disableCourier) {
                              toast({
                                title: 'Error',
                                description: 'Please enter the pinpoint location for this shipping',
                                variant: 'destructive',
                              });
                              return;
                            }

                            setSelectedShipping(String(courier.courier_service_id));
                            handleShippingSelect(courier.courier_service_id);
                          }}
                        >
                          <Label
                            htmlFor={String(courier.courier_service_id)}
                            className={cn(
                              'flex flex-1 justify-between',
                              disableCourier ? 'cursor-not-allowed' : 'cursor-pointer'
                            )}
                          >
                            <div className='flex w-full items-center space-x-4'>
                              {courier.courier_logo && (
                                <Image
                                  src={courier.courier_logo}
                                  alt={courier.courier_service_name}
                                  width={40}
                                  height={40}
                                />
                              )}
                              <div className='flex w-full items-center justify-between text-sm'>
                                <div className='space-y-2'>
                                  <div className='flex items-center gap-2'>
                                    <p className='font-medium'>{courier.courier_service_name}</p>
                                    {courier.is_cod_supported && (
                                      <Badge
                                        variant='custom'
                                        className='rounded-sm bg-[var(--background)] px-1.5 py-1 text-[12px] font-normal capitalize text-[var(--primary-text)]'
                                      >
                                        COD Support
                                      </Badge>
                                    )}
                                  </div>
                                  <p className='text-xs text-muted-foreground'>
                                    Estimate:{' '}
                                    {formatEstimateDelivery(courier.eta_from, courier.eta_to)}
                                  </p>
                                  <Badge
                                    variant='custom'
                                    className='mt-1 rounded-sm bg-[var(--background)] px-1.5 py-1 text-[12px] font-normal capitalize text-[var(--primary-text)]'
                                  >
                                    {courier.courier_service_category}
                                  </Badge>
                                </div>
                                <div className='flex items-center gap-2'>
                                  <p
                                    className={cn(
                                      'text-sm text-muted-foreground',
                                      (courier.promotion || courier.shipper_promotion) &&
                                        'line-through'
                                    )}
                                  >
                                    {formatIDR(courier.rates ?? 0)}
                                  </p>

                                  {(courier.promotion || courier.shipper_promotion) && (
                                    <p
                                      className={cn(
                                        'text-sm font-semibold',
                                        courier.final_rates === 0
                                          ? 'text-red-500'
                                          : 'text-muted-foreground'
                                      )}
                                    >
                                      {courier.final_rates === 0
                                        ? 'FREE'
                                        : formatIDR(courier.final_rates ?? 0)}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </div>
                          </Label>
                        </div>
                      );
                    })}
                    {listShipping.length === 0 && (
                      <div className='flex min-h-[50px] w-full items-center justify-center'>
                        <p className='text-sm'>No shipping available</p>
                      </div>
                    )}
                  </RadioGroup>
                </div>
              )}
            </>
          ) : (
            <>
              {isLoadingCategory ? (
                <div className='flex w-full items-center justify-center'>
                  <div className='flex w-full flex-col items-center justify-center gap-2'>
                    <Skeleton className='h-8 w-full rounded-sm bg-gray-200' />
                    <Skeleton className='h-8 w-full rounded-sm bg-gray-200' />
                    <Skeleton className='h-8 w-full rounded-sm bg-gray-200' />
                  </div>
                </div>
              ) : (
                listShippingCategory &&
                listShippingCategory?.data.length > 0 && (
                  <Accordion
                    type='single'
                    collapsible
                    className='w-full'
                    onValueChange={(v) => (!v ? resetSelection() : handleCategorySelect(v))}
                  >
                    {listShippingCategory?.data?.map((category) => {
                      const disableCourier =
                        (category.name.toLowerCase() === 'instant' ||
                          category.name.toLowerCase() === 'sameday') &&
                        disabled;
                      return (
                        <AccordionItem
                          value={category.service_category_id}
                          key={category.service_category_id}
                        >
                          <AccordionTrigger
                            className={cn(
                              'w-full flex-row-reverse justify-between rounded-lg p-3',
                              disableCourier
                                ? 'cursor-not-allowed'
                                : 'cursor-pointer hover:bg-accent'
                            )}
                            disabled={disableCourier}
                          >
                            <div
                              className={cn(
                                'justify-between text-sm font-medium',
                                disableCourier
                                  ? 'cursor-not-allowed text-gray-400'
                                  : 'cursor-pointer'
                              )}
                            >
                              {category.name}
                            </div>
                          </AccordionTrigger>
                          <AccordionContent>
                            {selectedCategory &&
                              (isLoadingShipping ? (
                                <div className='flex w-full flex-col items-center justify-center gap-2 px-2'>
                                  <Skeleton className='h-8 w-full rounded-sm bg-gray-200' />
                                  <Skeleton className='h-8 w-full rounded-sm bg-gray-200' />
                                  <Skeleton className='h-8 w-full rounded-sm bg-gray-200' />
                                </div>
                              ) : listShipping && listShipping.length > 0 ? (
                                <>
                                  <div className='max-h-96 overflow-y-auto px-2'>
                                    <RadioGroup value={selectedShipping}>
                                      {listShipping.map((courier) => (
                                        <div
                                          key={courier.courier_service_id}
                                          className='flex items-center space-x-2 rounded-lg border-b px-4 py-2 hover:bg-accent'
                                          onClick={() => {
                                            setSelectedShipping(String(courier.courier_service_id));
                                            handleShippingSelect(courier.courier_service_id);
                                          }}
                                        >
                                          <Label
                                            htmlFor={String(courier.courier_service_id)}
                                            className='flex flex-1 cursor-pointer justify-between'
                                          >
                                            <div className='flex w-full items-center space-x-4'>
                                              {courier.courier_logo && (
                                                <Image
                                                  src={courier.courier_logo}
                                                  alt={courier.courier_service_name}
                                                  width={40}
                                                  height={40}
                                                />
                                              )}
                                              <div className='flex w-full items-center justify-between text-sm'>
                                                <div className='space-y-2'>
                                                  <div className='flex items-center gap-2'>
                                                    <p className='font-medium'>
                                                      {courier.courier_service_name}
                                                    </p>
                                                    {courier.is_cod_supported && (
                                                      <Badge
                                                        variant='custom'
                                                        className='rounded-sm bg-[var(--primary-text)] px-1.5 py-1 text-[12px] font-normal text-[var(--background)]'
                                                      >
                                                        COD Support
                                                      </Badge>
                                                    )}
                                                  </div>
                                                  <p className='text-xs text-muted-foreground'>
                                                    Estimate:{' '}
                                                    {formatEstimateDelivery(
                                                      courier.eta_from,
                                                      courier.eta_to
                                                    )}
                                                  </p>
                                                </div>
                                                <div className='flex items-center gap-2'>
                                                  {/* Always show base rate */}
                                                  <p
                                                    className={cn(
                                                      'text-sm text-muted-foreground',
                                                      (courier.promotion ||
                                                        courier.shipper_promotion) &&
                                                        'line-through'
                                                    )}
                                                  >
                                                    {formatIDR(courier.rates ?? 0)}
                                                  </p>

                                                  {/* Show final price or FREE only if there is a promotion */}
                                                  {(courier.promotion ||
                                                    courier.shipper_promotion) && (
                                                    <p
                                                      className={cn(
                                                        'text-sm font-semibold',
                                                        courier.final_rates === 0
                                                          ? 'text-red-500'
                                                          : 'text-muted-foreground'
                                                      )}
                                                    >
                                                      {courier.final_rates === 0
                                                        ? 'FREE'
                                                        : formatIDR(courier.final_rates ?? 0)}
                                                    </p>
                                                  )}
                                                </div>
                                              </div>
                                            </div>
                                          </Label>
                                        </div>
                                      ))}
                                      {listShipping.length === 0 && (
                                        <div className='flex min-h-[50px] w-full items-center justify-center'>
                                          <p className='text-sm'>No shipping available</p>
                                        </div>
                                      )}
                                    </RadioGroup>
                                  </div>
                                </>
                              ) : (
                                <div className='flex min-h-[50px] w-full items-center justify-center'>
                                  <p className='text-sm'>No shipping available</p>
                                </div>
                              ))}
                          </AccordionContent>
                        </AccordionItem>
                      );
                    })}
                  </Accordion>
                )
              )}
            </>
          )}
        </div>
      </ModalResponsive>
    </>
  );
}
